var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Tracking the change of state of multiple transfers.
 *
 * Connects to imara's SSE stream and emits a `transfer` event every time the
 * service pushes a transfer update. The connection is opened immediately and
 * is re-established (with `Last-Event-ID`) whenever it drops, until `close()`
 * is called.
 *
 * NOTE: This is built on `fetch` rather than the native `EventSource`, because
 * `EventSource` cannot send the `Authorization` header used by the client.
 *
 * @example
 * const source = createTransfersEventsSource(client, { filter: { company } });
 * const off = source.on("transfer", (e) => console.log(e.data.id, e.data.status));
 * // later
 * off();
 * source.close();
 */
export function createTransfersEventsSource(client, options = {}) {
    var _a;
    const { filter, reconnect = true, retryInterval = 3000, maxRetryInterval = 30000, } = options;
    const controller = new AbortController();
    const listeners = {
        transfer: new Set(),
        open: new Set(),
        error: new Set(),
        state: new Set(),
    };
    let state = "connecting";
    let lastEventId;
    let baseRetry = retryInterval;
    function emit(type, value) {
        for (const fn of Array.from(listeners[type])) {
            try {
                fn(value);
            }
            catch (err) {
                console.error(`[imara/sse] '${type}' listener failed`, err);
            }
        }
    }
    function setState(next) {
        if (state === next)
            return;
        state = next;
        emit("state", next);
    }
    const url = client.url.joinPath("/v1/sse");
    url.searchParams.set("stream", "transfers");
    if (filter === null || filter === void 0 ? void 0 : filter.company)
        url.searchParams.set("company", filter.company);
    if (filter === null || filter === void 0 ? void 0 : filter.wallet)
        url.searchParams.set("wallet", filter.wallet);
    for (const id of (_a = filter === null || filter === void 0 ? void 0 : filter.transfers) !== null && _a !== void 0 ? _a : [])
        url.searchParams.append("id", id);
    function onMessage(msg) {
        if (msg.id !== undefined)
            lastEventId = msg.id;
        let data;
        try {
            data = JSON.parse(msg.data);
        }
        catch (err) {
            emit("error", err);
            return;
        }
        // Accept both named (`event: transfer.success`) and unnamed messages.
        let type = msg.event;
        if (!type || type === "message") {
            if (!(data === null || data === void 0 ? void 0 : data.status))
                return;
            type = `transfer.${data.status}`;
        }
        if (!type.startsWith("transfer."))
            return;
        emit("transfer", {
            type: type,
            eventId: msg.id,
            data,
        });
    }
    function run() {
        return __awaiter(this, void 0, void 0, function* () {
            let delay = baseRetry;
            while (!controller.signal.aborted) {
                setState("connecting");
                try {
                    const headers = new Headers({
                        Accept: "text/event-stream",
                        "Cache-Control": "no-cache",
                    });
                    if (lastEventId)
                        headers.set("Last-Event-ID", lastEventId);
                    // passing our own signal replaces the client's default request timeout
                    const res = yield client.fetch(url.href, {
                        method: "GET",
                        headers,
                        cache: "no-store",
                        signal: controller.signal,
                    });
                    if (!res.body) {
                        throw new Error("imara/sse: response has no body");
                    }
                    setState("open");
                    emit("open", undefined);
                    yield readEventStream(res.body, controller.signal, {
                        onMessage,
                        onRetry: (ms) => {
                            baseRetry = ms;
                        },
                    });
                    // a stream that ran successfully resets the backoff
                    delay = baseRetry;
                }
                catch (err) {
                    if (controller.signal.aborted)
                        break;
                    emit("error", err);
                    if (!isRetryable(err))
                        break;
                }
                if (!reconnect || controller.signal.aborted)
                    break;
                yield sleep(delay, controller.signal);
                delay = Math.min(delay * 2, maxRetryInterval);
            }
            setState("closed");
        });
    }
    run();
    return {
        get state() {
            return state;
        },
        on(type, listener) {
            listeners[type].add(listener);
            return () => {
                listeners[type].delete(listener);
            };
        },
        close() {
            controller.abort();
            setState("closed");
            for (const set of Object.values(listeners))
                set.clear();
        },
    };
}
/**
 * Errors that won't be fixed by reconnecting (auth failures that couldn't be
 * refreshed, or client errors such as 400/403/404).
 */
function isRetryable(err) {
    if (!err || typeof err !== "object")
        return true;
    const name = err.name;
    if (name === "UserMissingRefreshTokenError" ||
        name === "RefreshTokenExpiredError") {
        return false;
    }
    const status = err.statusCode;
    if (typeof status === "number" && status >= 400 && status < 500) {
        return status === 408 || status === 429;
    }
    return true;
}
function sleep(ms, signal) {
    return new Promise((resolve) => {
        if (signal.aborted)
            return resolve();
        const t = setTimeout(done, ms);
        signal.addEventListener("abort", done, { once: true });
        function done() {
            clearTimeout(t);
            signal.removeEventListener("abort", done);
            resolve();
        }
    });
}
/**
 * Minimal `text/event-stream` parser
 * @see https://html.spec.whatwg.org/multipage/server-sent-events.html#event-stream-interpretation
 */
function readEventStream(body, signal, handlers) {
    return __awaiter(this, void 0, void 0, function* () {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        const abort = () => reader.cancel().catch(() => { });
        signal.addEventListener("abort", abort, { once: true });
        let buffer = "";
        let event = "";
        let data = [];
        let id;
        const dispatch = () => {
            if (data.length > 0) {
                handlers.onMessage({ event, data: data.join("\n"), id });
            }
            event = "";
            data = [];
            id = undefined;
        };
        const processLine = (line) => {
            if (line === "")
                return dispatch();
            if (line.startsWith(":"))
                return; // comment / keep-alive
            const idx = line.indexOf(":");
            const field = idx === -1 ? line : line.slice(0, idx);
            let value = idx === -1 ? "" : line.slice(idx + 1);
            if (value.startsWith(" "))
                value = value.slice(1);
            switch (field) {
                case "event":
                    event = value;
                    break;
                case "data":
                    data.push(value);
                    break;
                case "id":
                    if (!value.includes("\0"))
                        id = value;
                    break;
                case "retry": {
                    const ms = Number(value);
                    if (Number.isInteger(ms) && ms >= 0)
                        handlers.onRetry(ms);
                    break;
                }
            }
        };
        try {
            while (true) {
                const { done, value } = yield reader.read();
                if (done)
                    break;
                buffer += decoder.decode(value, { stream: true });
                let match;
                const newline = /\r\n|\r|\n/g;
                let start = 0;
                while ((match = newline.exec(buffer)) !== null) {
                    // a trailing `\r` may be the first half of a `\r\n` split across chunks
                    if (match[0] === "\r" && match.index === buffer.length - 1)
                        break;
                    processLine(buffer.slice(start, match.index));
                    start = match.index + match[0].length;
                }
                buffer = buffer.slice(start);
            }
        }
        finally {
            signal.removeEventListener("abort", abort);
            reader.releaseLock();
        }
    });
}
